import React, { useEffect, useMemo, useRef, useState } from 'react';

import moment from 'moment-timezone';
import update from 'immutability-helper';
import { VelocityComponent } from 'velocity-react';

import AddressLookupField from 'views/AddressLookupField';
import LottieView from 'views/Lottie.js';
import Request from 'files/Request';
import TextField from 'views/TextField';
import Utils from 'files/Utils.js';
import { EVENTS, logBookingEvent } from 'files/Analytics.js';

const FIRE = '#D63D1F';
const FIRE_GLOW = '#FF6B3D';
const ACCENT_GREEN = '#2E8B57';
const SECONDARY_DARK = '#242F3F';
const MUTED_BG = '#F1F2F6';
const MUTED_TEXT = '#6F6F6F';
const BORDER = 'rgba(220,220,220,0.6)';
const DESTRUCTIVE = '#DC2626';

const validateDetails = details => {
    let errors = {};
    if(!details.first_name || details.first_name.trim().length < 2) {
        errors.first_name = true;
    }
    if(!details.last_name || details.last_name.trim().length < 2) {
        errors.last_name = true;
    }
    let email = (details.email_address || '').trim();
    if(!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || email.length > 255) {
        errors.email_address = true;
    }
    if(!details.phone_number) {
        errors.phone_number = true;
    }
    /*
    if(!details.address) {
        errors.address = true;
    }
    */
    if(!details.zipcode) {
        errors.zipcode = true;
    }
    return errors;
}

// Reads the dealership's daily operating window. The server returns hours keyed
// by program (e.g. `safety_associates`); the `default` key holds the general
// dealership hours used here. Each entry is `{ start, end }` as "HH:mm:ss".
const getOperatingHours = dealership => {
    let hours = dealership?.operating_hours?.default;
    if(!hours || !hours.start || !hours.end) {
        return null;
    }
    return hours;
}

// True when the current moment falls within the dealership's operating window.
// "Now" is evaluated in the dealership's timezone when provided, otherwise the
// visitor's local timezone. Unknown hours return false so the caller routes the
// visitor to the (always-available) "Call Me" flow rather than promising a live
// call into a closed office.
const isDealershipOpen = dealership => {
    let hours = getOperatingHours(dealership);
    if(!hours) {
        return false;
    }
    let tz = (dealership && dealership.timezone) || moment.tz.guess();
    let now = moment().tz(tz);
    let today = now.format('YYYY-MM-DD');
    let start = moment.tz(`${today} ${hours.start}`, 'YYYY-MM-DD HH:mm:ss', tz);
    let end = moment.tz(`${today} ${hours.end}`, 'YYYY-MM-DD HH:mm:ss', tz);
    return now.isSameOrAfter(start) && now.isBefore(end);
}

// Human-readable operating window (e.g. "9:00 AM – 5:00 PM") for display, or
// null when the dealership exposes no usable hours.
const formatOperatingHours = dealership => {
    let hours = getOperatingHours(dealership);
    if(!hours) {
        return null;
    }
    let start = moment(hours.start, 'HH:mm:ss').format('h:mm A');
    let end = moment(hours.end, 'HH:mm:ss').format('h:mm A');
    return `${start} – ${end}`;
}

const BookingLayer = ({ initialZip = null, mode = 'booking', utils }) => {

    const isMobile = Utils.isMobile();
    const isCallNow = mode === 'call_now';
    const isCanada = Utils.isCanadianVisitor();
    const callbackNumber = Utils.getPhoneNumber();

    const [availability, setAvailability] = useState({});
    const [dealership, setDealership] = useState(null);
    const [details, setDetails] = useState({
        //address: null,
        email_address: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        zipcode: initialZip || ''
    });
    const [errors, setErrors] = useState({});
    const [isCallback, setIsCallback] = useState(mode === 'callback');
    const [leadID, setLeadID] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null);
    const [opacity, setOpacity] = useState(0);
    const [scale, setScale] = useState(0.95);
    // the "Call Now" flow starts by asking only for a zipcode (no rent/own or
    // full lead form) so we can look up the local dealer and check its hours.
    const [step, setStep] = useState(isCallNow ? 'call_now_zip' : 1);
    const [expandedDay, setExpandedDay] = useState(null);

    // ensures the appointment-selected conversion event fires at most once
    const conversionLogged = useRef(false);

    // numeric position for the progress bar; the non-numeric "Call Now" steps
    // map onto the same two-step journey (zip lookup → result).
    const stepNumber = (step === 'rent' || step === 'call_now_zip') ? 1
        : (step === 'call_now_open' || step === 'call_now_closed') ? 2
        : step;

    // Randomly flags 25% of each day's slots as low-availability ("Only 1
    // Left") or "Fully Booked" to create urgency. Flagged slots are drawn only
    // from the front or last quarter of the day; middle slots always reflect
    // real availability metadata. Memoized on `availability` so the random
    // picks stay stable across re-renders.
    const slotMeta = useMemo(() => {
        let meta = {};
        Object.entries(availability).forEach(([day, slots]) => {
            let n = slots.length;
            let dayMeta = {};
            if(n > 0) {
                let quarter = Math.max(1, Math.round(n / 4));
                let eligible = [];
                for(let i = 0; i < n; i++) {
                    if(i < quarter || i >= n - quarter) {
                        eligible.push(i);
                    }
                }
                eligible = Array.from(new Set(eligible));
                let markCount = Math.min(Math.round(n * 0.25), eligible.length);
                for(let i = eligible.length - 1; i > 0; i--) {
                    let j = Math.floor(Math.random() * (i + 1));
                    [eligible[i], eligible[j]] = [eligible[j], eligible[i]];
                }
                eligible.slice(0, markCount).forEach(idx => {
                    dayMeta[slots[idx].start_time] = Math.random() < 0.5 ? 'one_left' : 'fully_booked';
                });
            }
            meta[day] = dayMeta;
        });
        return meta;
    }, [availability]);

    const onUpdateTarget = props => {
        setDetails(prev => update(prev, { $merge: props }));
        Object.keys(props).forEach(key => {
            if(errors[key]) {
                setErrors(prev => {
                    let next = { ...prev };
                    delete next[key];
                    return next;
                });
            }
        });
    }

    const onClose = () => {
        setOpacity(0);
        setScale(0.95);
        setTimeout(() => {
            utils.booking.hide();
        }, 250);
    }

    const onBackdropClick = evt => {
        if(evt.target === evt.currentTarget) {
            onClose();
        }
    }

    const onOwn = () => {
        // track booking-funnel step 3: home ownership selection
        logBookingEvent(EVENTS.HOME_OWNERSHIP, { ownership: 'own', funnel_step: 3 });
        setStep(2);
    }

    const onRent = () => {
        // track booking-funnel step 3: home ownership selection, canadian dealerships allow renters and unites states does not
        logBookingEvent(EVENTS.HOME_OWNERSHIP, { ownership: 'rent', funnel_step: 3 });
        setStep('rent');
    }

    const onDetailsSubmit = async (props, evt) => {
        try {

            // verify that no errors are present with the form inputs
            evt.preventDefault();
            if(loading) {
                return;
            }
            let foundErrors = validateDetails(details);
            if(Object.keys(foundErrors).length > 0) {
                setErrors(foundErrors);
                return;
            }

            // clear errors and enter the loading state for the submit button
            setErrors({});
            setLoading(true);

            // submit request to get actual servicing dealership
            let { availability, dealership } = await Request.get(utils, '/dealerships/', {
                address: details.zipcode,
                end_date: moment().add(4, 'days').format('YYYY-MM-DD'),
                location: details.location,
                start_date: moment().add(1, 'days').format('YYYY-MM-DD'),
                type: 'request_availability_for_location',
            });

            // submit request to server to book appointment. for the "Call Me"
            // flow we flag the lead so the server knows a dealer call-back was
            // requested (rather than a scheduled in-home inspection).
            let { id, do_not_call } = await Request.post(utils, '/leads/', {
                ...details,
                callback_requested: isCallback,
                channel: 'peak_seven_marketing',
                dealership_id: dealership.id,
                do_not_call_override: props?.do_not_call_override || false,
                type: 'new_from_customer'
            });

            // determine if the user was previously put on the do not call list
            if(do_not_call === true) {
                utils.alert.show({
                    title: 'Just a Second',
                    message: `It looks like we found your information on our Do Not Call list. This can happen if you've requested to opt out of communications from us in the past. Would you like to proceed anyway?`,
                    buttons: [{
                        key: 'confirm',
                        title: isCallback ? 'Yes, Proceed' : 'Yes, Proceed with Booking',
                        style: 'default'
                    },{
                        key: 'cancel',
                        title: 'Cancel',
                        style: 'cancel'
                    }],
                    onClick: key => {
                        if(key === 'confirm') {
                            onDetailsSubmit({ do_not_call_override: true }, evt);
                            return;
                        }
                    }
                });
                return;
            }

            setDealership(dealership);
            setLeadID(id);

            // track booking-funnel step 4: lead information submitted
            logBookingEvent(EVENTS.LEAD_INFO, { funnel_step: 4 });

            // "Call Me" flow ends at the call-back confirmation screen; the
            // booking flow continues to time-slot selection.
            if(isCallback) {
                logBookingEvent(EVENTS.CALLBACK_REQUESTED, { lead_id: id });
                setStep('callback_confirmed');
                return;
            }

            setAvailability(availability);
            setStep(3);

        } catch (e) {
            if(e.code === 409) {
                logBookingEvent(EVENTS.LEAD_OUT_OF_SERVICE_AREA, details);
            }
            if(e.code === 409) {
                logBookingEvent(EVENTS.SUBMISSION_ERROR, { message: e.message });
            }
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving available time slots. ${e.message}`
            });
        } finally {
            setLoading(false);
        }
    }

    // "Call Now" flow: validate the zipcode, look up the optimal local dealer
    // (without creating a lead), and branch on its operating hours — open dealers
    // get a click-to-call screen, closed dealers are routed to the "Call Me"
    // call-back flow so the visitor still gets reached.
    const onCallNowSubmit = async evt => {
        try {

            evt.preventDefault();
            if(loading) {
                return;
            }

            // the Call Now flow only collects a zipcode at this step
            let zipcode = (details.zipcode || '').trim();
            setErrors({});
            setLoading(true);

            // track booking-funnel step 1: zip code entered + dealer lookup
            logBookingEvent(EVENTS.SCHEDULE_INSPECTION, { form_location: 'call_now', funnel_step: 1 });

            // resolve the optimal servicing dealer for this zip so we can read
            // its operating hours (no lead is created at this stage)
            let { dealerships } = await Request.get(utils, '/dealerships/', {
                type: 'optimal_dealership_for_zipcode',
                zipcode: zipcode
            });

            // bail out if no dealer services the area
            if(!dealerships || dealerships.length === 0) {
                logBookingEvent(EVENTS.LEAD_OUT_OF_SERVICE_AREA, details);
                utils.alert.show({
                    title: 'No Dealers Found',
                    message: 'It looks like there are no authorized dealers in your area at the moment.'
                });
                return;
            }

            let nextDealership = dealerships[0];
            setDealership(nextDealership);
            setStep(isDealershipOpen(nextDealership) ? 'call_now_open' : 'call_now_closed');

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue finding a dealer near you. ${e.message}`
            });
        } finally {
            setLoading(false);
        }
    }

    // From the "closed" screen, hand the visitor off to the "Call Me" call-back
    // flow (rent/own → details → confirmation). The zipcode they already entered
    // carries over via `details`.
    const onSwitchToCallback = () => {
        setIsCallback(true);
        setErrors({});
        setStep(1);
    }

    // Click-to-call from the "open" screen; mirrors the Hero call_now tracking.
    const onCallNowDial = () => {
        logBookingEvent(EVENTS.PHONE_NUMBER_CLICK, { phone_number: callbackNumber, cta: 'call_now', form_location: 'booking' });
    }

    const onConfirm = async () => {
        try {

            if(!selected || loading) {
                return;
            }
            setLoading(true);

            // submit request to server to book appointment
            let { id } = await Request.post(utils, '/demos/', {
                ...details,
                channel: 'peak_seven_marketing',
                date: moment(`${selected.day} ${selected.time}`, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD HH:mm:ss'),
                dealership_id: dealership.id,
                lead_id: leadID,
                type: 'new_request_from_customer'
            });

            // animate components out of view and show confirmation screen
            setOpacity(0);
            setScale(0.95);
            setTimeout(() => {
                utils.booking.confirmed({ demo_request_id: id });
            }, 250);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue confirming your appointment. ${e.message}`
            });
        }
    }

    useEffect(() => {
        setTimeout(() => {
            setOpacity(1);
            setScale(1);
        }, 50);
    }, []);

    const getDayOfWeek = date => {
        let next_date = moment(date);
        if(moment().isSame(next_date, 'day')) {
            return 'Today';
        }
        if(moment().add(1, 'days').isSame(next_date, 'day')) {
            return 'Tomorrow';
        }
        return moment(date).format('dddd');
    }

    // Resolves how a slot should display. Slots flagged by slotMeta use the
    // randomized urgency state; everything else (the middle of each day, plus
    // any unflagged slots) reflects the real availability metadata.
    const getSlotDisplay = (day, slot) => {
        let flagged = slotMeta[day] && slotMeta[day][slot.start_time];
        if(flagged === 'one_left') {
            return { booked: false, badge: 'Only 1 Left' };
        }
        if(slot.available === 0 || flagged === 'fully_booked') {
            return { booked: true, badge: null };
        }
        if(slot.available <= 2) {
            return { booked: false, badge: `Only ${slot.available} Left` };
        }
        return { booked: false, badge: null };
    }

    const getProgressBar = () => {

        // the "Call Me" flow has no time-slot step, so it is a 2-step journey
        let totalSteps = isCallback || isCallNow ? 2 : 3;
        return (
            <div style={{ marginBottom: 20 }}>
                <div style={{
                    backgroundColor: MUTED_BG,
                    borderRadius: 999,
                    height: 6,
                    overflow: 'hidden',
                    width: '100%'
                }}>
                    <div style={{
                        background: `linear-gradient(90deg, ${FIRE}, ${FIRE_GLOW})`,
                        height: '100%',
                        transition: 'width 0.5s',
                        width: `${(Number(stepNumber) / totalSteps) * 100}%`
                    }} />
                </div>
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: 8
                }}>
                    <span style={{
                        color: FIRE,
                        fontFamily: '"Poppins", sans-serif',
                        fontSize: 12,
                        fontWeight: 600,
                        letterSpacing: 1.5,
                        textTransform: 'uppercase'
                    }}>{`Step ${stepNumber} of ${totalSteps}`}</span>
                </div>
            </div>
        )
    }

    const getStep1 = () => {
        return (
            <div style={{ textAlign: 'center' }}>
                <p style={{
                    color: SECONDARY_DARK,
                    fontFamily: '"Lato", sans-serif',
                    fontSize: 16,
                    lineHeight: 1.6,
                    margin: '0 0 16px 0'
                }}>{'Let\'s match you with a local OmniShield expert so you can claim your free Emergency Fire Blanket.'}</p>
                <h4 style={{
                    color: SECONDARY_DARK,
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: isMobile ? 22 : 26,
                    fontWeight: 600,
                    margin: '0 0 8px 0'
                }}>{'Do you rent or own your home?'}</h4>
                <p style={{
                    color: MUTED_TEXT,
                    fontFamily: '"Lato", sans-serif',
                    fontSize: 16,
                    margin: '0 0 24px 0'
                }}>{'Quick question to make sure we\'re a fit.'}</p>
                <div style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    flexWrap: 'wrap',
                    gap: 12,
                    justifyContent: 'center'
                }}>
                    <button
                    onClick={onOwn}
                    style={{
                        alignItems: 'center',
                        background: `linear-gradient(90deg, ${FIRE}, ${FIRE_GLOW})`,
                        border: 'none',
                        borderRadius: 999,
                        boxShadow: `0 4px 12px rgba(214,61,31,0.3)`,
                        color: '#FFFFFF',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        fontFamily: '"Poppins", sans-serif',
                        fontSize: 18,
                        fontWeight: 600,
                        gap: 8,
                        justifyContent: 'center',
                        padding: '16px 36px',
                        transition: 'transform 0.2s'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
                        <i className={'fas fa-house'} style={{ fontSize: 18 }} />
                        {'Own'}
                    </button>
                    <button
                    onClick={onRent}
                    style={{
                        alignItems: 'center',
                        backgroundColor: MUTED_BG,
                        border: 'none',
                        borderRadius: 999,
                        color: SECONDARY_DARK,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        fontFamily: '"Poppins", sans-serif',
                        fontSize: 18,
                        fontWeight: 600,
                        gap: 8,
                        justifyContent: 'center',
                        padding: '16px 36px',
                        transition: 'transform 0.2s'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
                        <i className={'fas fa-building'} style={{ fontSize: 18 }} />
                        {'Rent'}
                    </button>
                </div>
            </div>
        )
    }

    const getStepRent = () => {
        return (
            <div style={{ textAlign: 'center' }}>
                <h4 style={{
                    color: SECONDARY_DARK,
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: 18,
                    fontWeight: 600,
                    margin: '0 0 12px 0'
                }}>{'We\'re sorry'}</h4>
                <p style={{
                    color: MUTED_TEXT,
                    fontFamily: '"Lato", sans-serif',
                    fontSize: 14,
                    lineHeight: 1.6,
                    margin: '0 0 20px 0'
                }}>{'We can only serve homeowners at this time. If you\'re currently renting a house or live in an apartment building, you can ask your landlord to contact us for a free home safety inspection.'}</p>
                <button
                onClick={onClose}
                style={{
                    backgroundColor: 'transparent',
                    border: `1px solid ${BORDER}`,
                    borderRadius: 999,
                    color: SECONDARY_DARK,
                    cursor: 'pointer',
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: 14,
                    fontWeight: 600,
                    padding: '10px 24px'
                }}>{'Close'}</button>
            </div>
        )
    }

    const getInput = (field, props = {}) => {
        if(field === 'address') {
            /*
            return (
                <AddressLookupField
                fieldContanerStyle={{
                    border: `1px solid ${errors[field] ? DESTRUCTIVE : BORDER}`,
                }}
                onChange={result => {
                    onUpdateTarget({
                        address: result,
                        location: result.location
                    });
                }} 
                utils={utils}
                value={details.address && {
                    address: details.address,
                    location: details.location
                }} />
            )
            */
        }
        if(field === 'phone_number') {
            return (
                <TextField
                containerStyle={{
                    border: `1px solid ${errors[field] ? DESTRUCTIVE : BORDER}`,
                }}
                format={'phone_number'}
                onChange={text => onUpdateTarget({ [field]: text })}
                value={details[field] || ''}
                />
            )
        }
        return (
            <input
            onChange={evt => onUpdateTarget({ [field]: evt.target.value })}
            value={details[field] || ''}
            {...props}
            style={{
                backgroundColor: '#FFFFFF',
                border: `1px solid ${errors[field] ? DESTRUCTIVE : BORDER}`,
                borderRadius: 8,
                color: SECONDARY_DARK,
                fontFamily: '"Open Sans", sans-serif',
                fontSize: 16,
                height: 48,
                outline: 'none',
                padding: '0 20px',
                ...props.style
            }} />
        )
    }

    // dots loading indicator shown inside a submit button while its request is
    // in flight (mirrors the loading treatment in views/Button.js)
    const getLoadingOverlay = () => {
        return (
            <div style={{
                alignItems: 'center',
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                left: 0,
                position: 'absolute',
                right: 0,
                top: 0
            }}>
                <LottieView
                autoPlay={true}
                loop={true}
                source={require('files/lottie/dots-white.json')}
                style={{ height: 25, width: 25 }} />
            </div>
        )
    }

    const getStep2 = () => {
        return (
            <form onSubmit={onDetailsSubmit.bind(this, {})}>
                <div style={{
                    marginBottom: 8,
                    textAlign: 'center'
                }}>
                    <h4 style={{
                        color: SECONDARY_DARK,
                        fontFamily: '"Poppins", sans-serif',
                        fontSize: isMobile ? 22 : 26,
                        fontWeight: 600,
                        margin: '0 0 4px 0'
                    }}>{'Enter Your Details'}</h4>
                    <p style={{
                        color: MUTED_TEXT,
                        fontFamily: '"Lato", sans-serif',
                        fontSize: 16,
                        margin: 0
                    }}>{(isCallback || isCallNow)
                        ? 'We need this to pair you with your local authorized dealer so they can call you about your free kit.'
                        : 'This helps us match you with your local home-safety experts.'}</p>
                </div>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 16,
                    marginTop: 16
                }}>
                    <div style={{
                        display: 'grid',
                        gap: 12,
                        gridTemplateColumns: Utils.isMobile() ? '1fr' : '1fr 1fr'
                    }}>
                        {getInput('first_name', { placeholder: 'First name', autoComplete: 'given-name' })}
                        {getInput('last_name', { placeholder: 'Last name', autoComplete: 'family-name' })}
                    </div>
                    {getInput('email_address', { placeholder: 'Email Address (example@email.com)', type: 'email', autoComplete: 'email' })}
                    {getInput('phone_number')}
                    {initialZip === null && getInput('zipcode', { placeholder: isCanada ? 'Postal Code' : 'Zipcode', autoComplete: 'postal-code' })}
                    {Object.keys(errors).length > 0 && (
                        <p style={{
                            color: DESTRUCTIVE,
                            fontFamily: '"Open Sans", sans-serif',
                            fontSize: 14,
                            margin: 0,
                            textAlign: 'center'
                        }}>{'Please complete all fields correctly before continuing.'}</p>
                    )}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        paddingTop: 8
                    }}>
                        <button
                        type={'submit'}
                        disabled={loading}
                        style={{
                            alignItems: 'center',
                            background: `linear-gradient(90deg, ${FIRE}, ${FIRE_GLOW})`,
                            border: 'none',
                            borderRadius: 999,
                            color: '#FFFFFF',
                            cursor: loading ? 'default' : 'pointer',
                            display: 'inline-flex',
                            fontFamily: '"Poppins", sans-serif',
                            fontSize: 16,
                            fontWeight: 600,
                            gap: 8,
                            height: 48,
                            justifyContent: 'center',
                            overflow: 'hidden',
                            padding: '0 40px',
                            position: 'relative',
                            transition: 'transform 0.2s'
                        }}
                        onMouseEnter={e => { if(!loading) { e.currentTarget.style.transform = 'scale(1.1)'; } }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
                            <span style={{
                                alignItems: 'center',
                                display: 'inline-flex',
                                gap: 8,
                                opacity: loading ? 0 : 1
                            }}>
                                {'Continue'}
                                <i className={'fas fa-arrow-right'} style={{ fontSize: 14 }} />
                            </span>
                            {loading && getLoadingOverlay()}
                        </button>
                    </div>
                </div>
            </form>
        )
    }

    const getStep3 = () => {
        let now = moment().unix();
        return (
            <div style={{ textAlign: 'center' }}>
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 8,
                    justifyContent: 'center',
                    marginBottom: 12
                }}>
                    <i className={'fas fa-check'} style={{ color: ACCENT_GREEN, fontSize: 24 }} />
                    <h4 style={{
                        color: SECONDARY_DARK,
                        fontFamily: '"Poppins", sans-serif',
                        fontSize: isMobile ? 22 : 26,
                        fontWeight: 600,
                        margin: 0
                    }}>{'Pick a Time'}</h4>
                </div>
                <p style={{
                    color: MUTED_TEXT,
                    fontFamily: '"Lato", sans-serif',
                    fontSize: 16,
                    margin: '0 0 16px 0'
                }}>{`Hi ${details.first_name}, select a time for your free in-home safety inspection.`}</p>
                <div style={{
                    maxHeight: 320,
                    overflowY: 'auto',
                    padding: '0 4px',
                    textAlign: 'left'
                }}>
                    {Object.entries(availability).map(([key, slots], idx) => {

                        let priority = idx <= 1;
                        let targets = slots.filter(slot => {
                            let startTime = moment(`${key} ${slot.start_time}`, 'YYYY-MM-DD HH:mm:ss');
                            return startTime.unix() >= now;
                        });
                        if(targets.length === 0) {
                            return null;
                        }
                        let isExpanded = expandedDay === key;
                        return (
                            <div
                            key={key}
                            style={{
                                borderTop: idx === 0 ? 'none' : `1px solid ${BORDER}`,
                                padding: '16px 0'
                            }}>
                                <button
                                type={'button'}
                                onClick={() => setExpandedDay(isExpanded ? null : key)}
                                style={{
                                    alignItems: 'center',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: 8,
                                    padding: 0,
                                    width: '100%'
                                }}>
                                    <span style={{
                                        color: priority ? FIRE : SECONDARY_DARK,
                                        fontFamily: '"Poppins", sans-serif',
                                        fontSize: 16,
                                        fontWeight: 600
                                    }}>{getDayOfWeek(key)}</span>
                                    <span style={{
                                        color: MUTED_TEXT,
                                        fontFamily: '"Lato", sans-serif',
                                        fontSize: 14
                                    }}>{moment(key).format('MMM D')}</span>
                                    <i
                                    className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}
                                    style={{
                                        color: MUTED_TEXT,
                                        fontSize: 12,
                                        marginLeft: 'auto'
                                    }} />
                                </button>
                                {isExpanded && (
                                    <div 
                                    className={'row mx-0 mb-0'}
                                    style={{
                                        marginTop: 12,
                                        width: '100%'
                                    }}>
                                        {targets.map(slot => {
                                            let active = selected && selected.day === key && selected.time === slot.start_time;
                                            let startTime = moment(`${key} ${slot.start_time}`, 'YYYY-MM-DD HH:mm:ss');
                                            let display = getSlotDisplay(key, slot);
                                            return (
                                                <div
                                                className={'col-12 col-md-3 p-2'}
                                                key={slot.start_time}
                                                style={{
                                                    alignItems: 'center',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    width: '100%'
                                                }}>
                                                    <button
                                                    type={'button'}
                                                    disabled={display.booked}
                                                    onClick={() => {
                                                        setSelected({ day: key, time: slot.start_time });
                                                        // track booking-funnel step 5 (CONVERSION): appointment
                                                        // date/time selected — logged once per booking session
                                                        if(!conversionLogged.current) {
                                                            conversionLogged.current = true;
                                                            logBookingEvent(EVENTS.APPOINTMENT_SELECTED, {
                                                                funnel_step: 5,
                                                                appointment_date: key,
                                                                appointment_time: slot.start_time
                                                            });
                                                        }
                                                    }}
                                                    style={{
                                                        backgroundColor: display.booked ? MUTED_BG : (active ? 'rgba(46,139,87,0.1)' : '#FFFFFF'),
                                                        border: `2px solid ${active ? ACCENT_GREEN : BORDER}`,
                                                        borderRadius: 999,
                                                        color: display.booked ? MUTED_TEXT : (active ? ACCENT_GREEN : SECONDARY_DARK),
                                                        cursor: display.booked ? 'not-allowed' : 'pointer',
                                                        fontFamily: '"Poppins", sans-serif',
                                                        fontSize: 14,
                                                        fontWeight: 500,
                                                        maxWidth: '100%',
                                                        overflow: 'hidden',
                                                        padding: '8px 16px',
                                                        textDecoration: display.booked ? 'line-through' : 'none',
                                                        transition: 'all 0.15s',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        width: '100%'
                                                    }}>{startTime.format('h:mm A')}</button>
                                                    {display.badge && (
                                                        <span style={{
                                                            color: 'rgba(220,38,38,0.8)',
                                                            fontFamily: '"Lato", sans-serif',
                                                            fontSize: 10,
                                                            fontWeight: 600,
                                                            letterSpacing: 0.5,
                                                            marginTop: 4,
                                                            textTransform: 'uppercase'
                                                        }}>{display.badge}</span>
                                                    )}
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
                {selected && (
                    <p style={{
                        borderTop: `1px solid ${BORDER}`,
                        color: SECONDARY_DARK,
                        fontFamily: '"Lato", sans-serif',
                        fontSize: 16,
                        marginBottom: 16,
                        paddingTop: 16
                    }}>
                        {'Selected: '}
                        <span style={{ fontWeight: 600 }}>{`${moment(`${selected.day} ${selected.time}`, 'YYYY-MM-DD HH:mm:ss').format('MMM D [at] h:mm A')}`}</span>
                    </p>
                )}
                <div style={{
                    borderTop: `1px solid ${BORDER}`,
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: 16
                }}>
                    <button
                    disabled={!selected || loading}
                    onClick={onConfirm}
                    style={{
                        alignItems: 'center',
                        background: selected ? `linear-gradient(90deg, ${FIRE}, ${FIRE_GLOW})` : MUTED_BG,
                        border: 'none',
                        borderRadius: 999,
                        color: selected ? '#FFFFFF' : MUTED_TEXT,
                        cursor: !selected || loading ? 'not-allowed' : 'pointer',
                        display: 'inline-flex',
                        fontFamily: '"Poppins", sans-serif',
                        fontSize: 16,
                        fontWeight: 600,
                        gap: 8,
                        height: 48,
                        justifyContent: 'center',
                        overflow: 'hidden',
                        padding: '0 32px',
                        position: 'relative',
                        transition: 'transform 0.2s'
                    }}
                    onMouseEnter={e => { if(selected && !loading) e.currentTarget.style.transform = 'scale(1.1)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
                        <span style={{
                            alignItems: 'center',
                            display: 'inline-flex',
                            gap: 8,
                            opacity: loading ? 0 : 1
                        }}>
                            <i className={'fas fa-calendar'} style={{ fontSize: 14 }} />
                            {'Confirm'}
                        </span>
                        {loading && getLoadingOverlay()}
                    </button>
                </div>
            </div>
        )
    }

    const onDownloadContact = () => {
        Utils.downloadVCard({
            name: dealership?.name || 'OmniShield Authorized Dealer',
            org: dealership?.name || 'OmniShield',
            phone: callbackNumber
        });
    }

    const getCallbackConfirmation = () => {
        return (
            <div style={{ textAlign: 'center' }}>
                <div style={{
                    alignItems: 'center',
                    backgroundColor: 'rgba(46,139,87,0.1)',
                    borderRadius: '50%',
                    display: 'flex',
                    height: 56,
                    justifyContent: 'center',
                    margin: '0 auto 16px auto',
                    width: 56
                }}>
                    <i className={'fas fa-phone-volume'} style={{ color: ACCENT_GREEN, fontSize: 26 }} />
                </div>
                <h4 style={{
                    color: SECONDARY_DARK,
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: isMobile ? 22 : 26,
                    fontWeight: 600,
                    margin: '0 0 8px 0'
                }}>{'You\'re all set!'}</h4>
                <p style={{
                    color: MUTED_TEXT,
                    fontFamily: '"Lato", sans-serif',
                    fontSize: 16,
                    lineHeight: 1.6,
                    margin: '0 auto 24px auto',
                    maxWidth: 420
                }}>
                    {`Hi ${details.first_name}, we'll be calling you shortly from `}
                    <span style={{ color: SECONDARY_DARK, fontWeight: 700, whiteSpace: 'nowrap' }}>{callbackNumber}</span>
                    {'. Save our number to your contacts so the call doesn\'t get marked as spam. You\'ll need to answer to claim your free Emergency Fire Blanket.'}
                </p>
                <div style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: 12,
                    justifyContent: 'center'
                }}>
                    <button
                    type={'button'}
                    onClick={onDownloadContact}
                    style={{
                        alignItems: 'center',
                        background: `linear-gradient(90deg, ${FIRE}, ${FIRE_GLOW})`,
                        border: 'none',
                        borderRadius: 999,
                        color: '#FFFFFF',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        fontFamily: '"Poppins", sans-serif',
                        fontSize: 16,
                        fontWeight: 600,
                        gap: 8,
                        justifyContent: 'center',
                        padding: '12px 28px',
                        transition: 'transform 0.2s'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
                        <i className={'fas fa-address-book'} style={{ fontSize: 16 }} />
                        {'Add to Contacts'}
                    </button>
                    <button
                    type={'button'}
                    onClick={onClose}
                    style={{
                        backgroundColor: 'transparent',
                        border: `1px solid ${BORDER}`,
                        borderRadius: 999,
                        color: SECONDARY_DARK,
                        cursor: 'pointer',
                        fontFamily: '"Poppins", sans-serif',
                        fontSize: 16,
                        fontWeight: 600,
                        padding: '12px 28px'
                    }}>{'Done'}</button>
                </div>
            </div>
        )
    }

    // "Call Now" step 1: collect just a zipcode so we can resolve the local
    // dealer and inspect its operating hours.
    const getCallNowZip = () => {
        return (
            <form onSubmit={onCallNowSubmit}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{
                        alignItems: 'center',
                        background: `linear-gradient(135deg, ${FIRE}, ${FIRE_GLOW})`,
                        borderRadius: '50%',
                        display: 'flex',
                        height: 56,
                        justifyContent: 'center',
                        margin: '0 auto 16px auto',
                        width: 56
                    }}>
                        <i className={'fas fa-phone'} style={{ color: '#FFFFFF', fontSize: 24 }} />
                    </div>
                    <h4 style={{
                        color: SECONDARY_DARK,
                        fontFamily: '"Poppins", sans-serif',
                        fontSize: isMobile ? 22 : 26,
                        fontWeight: 600,
                        margin: '0 0 8px 0'
                    }}>{'Call Your Local Dealer'}</h4>
                    <p style={{
                        color: MUTED_TEXT,
                        fontFamily: '"Lato", sans-serif',
                        fontSize: 16,
                        lineHeight: 1.6,
                        margin: '0 auto 20px auto',
                        maxWidth: 420
                    }}>{`Enter your ${isCanada ? 'postal code' : 'ZIP code'} and we'll connect you with your nearest authorized dealer about your free Emergency Fire Blanket.`}</p>
                </div>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 16
                }}>
                    {getInput('zipcode', { placeholder: isCanada ? 'postal code' : 'ZIP code', autoComplete: 'postal-code', inputMode: 'numeric' })}
                    {errors.zipcode && (
                        <p style={{
                            color: DESTRUCTIVE,
                            fontFamily: '"Open Sans", sans-serif',
                            fontSize: 14,
                            margin: 0,
                            textAlign: 'center'
                        }}>{`Please enter a valid 5-digit ${isCanada ? 'postal code' : 'ZIP code'}.`}</p>
                    )}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        paddingTop: 8
                    }}>
                        <button
                        type={'submit'}
                        disabled={loading}
                        style={{
                            alignItems: 'center',
                            background: `linear-gradient(90deg, ${FIRE}, ${FIRE_GLOW})`,
                            border: 'none',
                            borderRadius: 999,
                            color: '#FFFFFF',
                            cursor: loading ? 'default' : 'pointer',
                            display: 'inline-flex',
                            fontFamily: '"Poppins", sans-serif',
                            fontSize: 16,
                            fontWeight: 600,
                            gap: 8,
                            height: 48,
                            justifyContent: 'center',
                            overflow: 'hidden',
                            padding: '0 40px',
                            position: 'relative',
                            transition: 'transform 0.2s'
                        }}
                        onMouseEnter={e => { if(!loading) { e.currentTarget.style.transform = 'scale(1.1)'; } }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
                            <span style={{
                                alignItems: 'center',
                                display: 'inline-flex',
                                gap: 8,
                                opacity: loading ? 0 : 1
                            }}>
                                {'Find My Dealer'}
                                <i className={'fas fa-arrow-right'} style={{ fontSize: 14 }} />
                            </span>
                            {loading && getLoadingOverlay()}
                        </button>
                    </div>
                </div>
            </form>
        )
    }

    // "Call Now" result — dealer is open: surface the number to call right now.
    const getCallNowOpen = () => {
        let dialNumber = `tel:+${('' + callbackNumber).replace(/\D/g, '')}`;
        let hoursLabel = formatOperatingHours(dealership);
        return (
            <div style={{ textAlign: 'center' }}>
                <div style={{
                    alignItems: 'center',
                    backgroundColor: 'rgba(46,139,87,0.1)',
                    borderRadius: '50%',
                    display: 'flex',
                    height: 56,
                    justifyContent: 'center',
                    margin: '0 auto 16px auto',
                    width: 56
                }}>
                    <i className={'fas fa-phone'} style={{ color: ACCENT_GREEN, fontSize: 26 }} />
                </div>
                <h4 style={{
                    color: SECONDARY_DARK,
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: isMobile ? 22 : 26,
                    fontWeight: 600,
                    margin: '0 0 8px 0'
                }}>{'We\'re Open — Call Now!'}</h4>
                <p style={{
                    color: MUTED_TEXT,
                    fontFamily: '"Lato", sans-serif',
                    fontSize: 16,
                    lineHeight: 1.6,
                    margin: '0 auto 16px auto',
                    maxWidth: 420
                }}>
                    {dealership && dealership.name
                        ? `${dealership.name} is open and ready to help you claim your free Emergency Fire Blanket. Give us a call:`
                        : 'Your local authorized dealer is open and ready to help you claim your free Emergency Fire Blanket. Give us a call:'}
                </p>
                <a
                href={dialNumber}
                onClick={onCallNowDial}
                style={{
                    color: FIRE,
                    display: 'inline-block',
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: 30,
                    fontWeight: 800,
                    letterSpacing: 0.5,
                    marginBottom: hoursLabel ? 8 : 20,
                    textDecoration: 'none'
                }}>{callbackNumber}</a>
                {hoursLabel && (
                    <p style={{
                        color: MUTED_TEXT,
                        fontFamily: '"Lato", sans-serif',
                        fontSize: 14,
                        margin: '0 0 20px 0'
                    }}>{`Today's hours: ${hoursLabel}`}</p>
                )}
                <div style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: 12,
                    justifyContent: 'center'
                }}>
                    <a
                    href={dialNumber}
                    onClick={onCallNowDial}
                    style={{
                        alignItems: 'center',
                        background: `linear-gradient(90deg, ${FIRE}, ${FIRE_GLOW})`,
                        border: 'none',
                        borderRadius: 999,
                        color: '#FFFFFF',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        fontFamily: '"Poppins", sans-serif',
                        fontSize: 16,
                        fontWeight: 600,
                        gap: 8,
                        justifyContent: 'center',
                        padding: '12px 28px',
                        textDecoration: 'none',
                        transition: 'transform 0.2s'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
                        <i className={'fas fa-phone'} style={{ fontSize: 16 }} />
                        {'Call Now'}
                    </a>
                    <button
                    type={'button'}
                    onClick={onClose}
                    style={{
                        backgroundColor: 'transparent',
                        border: `1px solid ${BORDER}`,
                        borderRadius: 999,
                        color: SECONDARY_DARK,
                        cursor: 'pointer',
                        fontFamily: '"Poppins", sans-serif',
                        fontSize: 16,
                        fontWeight: 600,
                        padding: '12px 28px'
                    }}>{'Done'}</button>
                </div>
            </div>
        )
    }

    // "Call Now" result — dealer is closed: explain and route to the "Call Me"
    // call-back flow so the visitor still gets reached.
    const getCallNowClosed = () => {
        let hoursLabel = formatOperatingHours(dealership);
        return (
            <div style={{ textAlign: 'center' }}>
                <div style={{
                    alignItems: 'center',
                    backgroundColor: MUTED_BG,
                    borderRadius: '50%',
                    display: 'flex',
                    height: 56,
                    justifyContent: 'center',
                    margin: '0 auto 16px auto',
                    width: 56
                }}>
                    <i className={'fas fa-clock'} style={{ color: MUTED_TEXT, fontSize: 26 }} />
                </div>
                <h4 style={{
                    color: SECONDARY_DARK,
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: isMobile ? 22 : 26,
                    fontWeight: 600,
                    margin: '0 0 8px 0'
                }}>{'We\'re Currently Closed'}</h4>
                <p style={{
                    color: MUTED_TEXT,
                    fontFamily: '"Lato", sans-serif',
                    fontSize: 16,
                    lineHeight: 1.6,
                    margin: '0 auto 24px auto',
                    maxWidth: 420
                }}>
                    {hoursLabel
                        ? `Your local authorized dealer is closed right now (open ${hoursLabel}). Leave your details and they'll call you back as soon as they're open to help you claim your free Emergency Fire Blanket.`
                        : 'Your local authorized dealer is closed right now. Leave your details and they\'ll call you back as soon as they\'re open to help you claim your free Emergency Fire Blanket.'}
                </p>
                <div style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: 12,
                    justifyContent: 'center'
                }}>
                    <button
                    type={'button'}
                    onClick={onSwitchToCallback}
                    style={{
                        alignItems: 'center',
                        background: `linear-gradient(90deg, ${FIRE}, ${FIRE_GLOW})`,
                        border: 'none',
                        borderRadius: 999,
                        color: '#FFFFFF',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        fontFamily: '"Poppins", sans-serif',
                        fontSize: 16,
                        fontWeight: 600,
                        gap: 8,
                        justifyContent: 'center',
                        padding: '12px 28px',
                        transition: 'transform 0.2s'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
                        <i className={'fas fa-phone-volume'} style={{ fontSize: 16 }} />
                        {'Request a Call'}
                    </button>
                    <button
                    type={'button'}
                    onClick={onClose}
                    style={{
                        backgroundColor: 'transparent',
                        border: `1px solid ${BORDER}`,
                        borderRadius: 999,
                        color: SECONDARY_DARK,
                        cursor: 'pointer',
                        fontFamily: '"Poppins", sans-serif',
                        fontSize: 16,
                        fontWeight: 600,
                        padding: '12px 28px'
                    }}>{'Close'}</button>
                </div>
            </div>
        )
    }

    return (
        <div
        onClick={onBackdropClick}
        style={{
            alignItems: 'center',
            backgroundColor: 'rgba(15,20,28,0.65)',
            display: 'flex',
            height: '100%',
            justifyContent: 'center',
            left: 0,
            overflow: 'auto',
            padding: isMobile ? 0 : '32px 16px',
            position: 'fixed',
            top: 0,
            width: '100%',
            zIndex: 9900
        }}>
            <VelocityComponent
            animation={{ opacity: opacity, scale: scale }}
            duration={250}
            easing={[250, 20]}>
                <div style={{
                    backgroundColor: '#FFFFFF',
                    border: `2px solid ${FIRE}`,
                    borderRadius: isMobile ? 0 : 24,
                    boxShadow: `0 0 0 4px rgba(214,61,31,0.15)`,
                    maxHeight: isMobile ? '100%' : 'calc(100vh - 64px)',
                    maxWidth: 576,
                    overflow: 'auto',
                    padding: '32px',
                    position: 'relative',
                    width: '100%'
                }}>
                    {!['callback_confirmed', 'call_now_open', 'call_now_closed'].includes(step) && getProgressBar()}
                    {step === 'call_now_zip' && getCallNowZip()}
                    {step === 'call_now_open' && getCallNowOpen()}
                    {step === 'call_now_closed' && getCallNowClosed()}
                    {step === 1 && getStep1()}
                    {step === 'rent' && getStepRent()}
                    {step === 2 && getStep2()}
                    {step === 3 && getStep3()}
                    {step === 'callback_confirmed' && getCallbackConfirmation()}
                </div>
            </VelocityComponent>
        </div>
    )
}

export default BookingLayer;
